import { toDisplayedRgb } from './gameMixColor.utils';

// sRGB to D50 CIE Lab, followed by CIEDE2000 (unit viewing-condition weights).
// Reference: https://www.w3.org/TR/css-color-4/#color-difference
const toLab = (color) => {
  const { r, g, b } = toDisplayedRgb(color);
  const linearize = (channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const red = linearize(r);
  const green = linearize(g);
  const blue = linearize(b);
  const x = (0.4360747 * red + 0.3850649 * green + 0.1430804 * blue) / 0.96422;
  const y = 0.2225045 * red + 0.7168786 * green + 0.0606169 * blue;
  const z = (0.0139322 * red + 0.0971045 * green + 0.7141733 * blue) / 0.82521;
  const f = (value) => (value > 216 / 24389 ? Math.cbrt(value) : (24389 / 27 * value + 16) / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
};

const radians = (degrees) => degrees * Math.PI / 180;
const degrees = (angle) => angle * 180 / Math.PI;
const hue = (a, b) => (degrees(Math.atan2(b, a)) + 360) % 360;

export const deltaE2000 = ([l1, a1, b1], [l2, a2, b2]) => {
  const c1 = Math.hypot(a1, b1);
  const c2 = Math.hypot(a2, b2);
  const averageC = (c1 + c2) / 2;
  const averageC7 = averageC ** 7;
  const g = 0.5 * (1 - Math.sqrt(averageC7 / (averageC7 + 25 ** 7)));
  const adjustedA1 = (1 + g) * a1;
  const adjustedA2 = (1 + g) * a2;
  const adjustedC1 = Math.hypot(adjustedA1, b1);
  const adjustedC2 = Math.hypot(adjustedA2, b2);
  const h1 = hue(adjustedA1, b1);
  const h2 = hue(adjustedA2, b2);
  const hueDifference = h2 - h1;
  const chromatic = adjustedC1 * adjustedC2 > 0;
  const deltaHue = !chromatic ? 0
    : hueDifference > 180 ? hueDifference - 360
      : hueDifference < -180 ? hueDifference + 360 : hueDifference;
  const deltaL = l2 - l1;
  const deltaC = adjustedC2 - adjustedC1;
  const deltaH = 2 * Math.sqrt(adjustedC1 * adjustedC2) * Math.sin(radians(deltaHue / 2));
  const meanL = (l1 + l2) / 2;
  const meanC = (adjustedC1 + adjustedC2) / 2;
  const meanHue = !chromatic ? h1 + h2
    : Math.abs(hueDifference) <= 180 ? (h1 + h2) / 2
      : h1 + h2 < 360 ? (h1 + h2 + 360) / 2 : (h1 + h2 - 360) / 2;
  const t = 1
    - 0.17 * Math.cos(radians(meanHue - 30))
    + 0.24 * Math.cos(radians(2 * meanHue))
    + 0.32 * Math.cos(radians(3 * meanHue + 6))
    - 0.20 * Math.cos(radians(4 * meanHue - 63));
  const deltaTheta = 30 * Math.exp(-(((meanHue - 275) / 25) ** 2));
  const meanC7 = meanC ** 7;
  const rotation = -2 * Math.sqrt(meanC7 / (meanC7 + 25 ** 7)) * Math.sin(radians(2 * deltaTheta));
  const lightnessWeight = 1 + 0.015 * (meanL - 50) ** 2 / Math.sqrt(20 + (meanL - 50) ** 2);
  const chromaWeight = 1 + 0.045 * meanC;
  const hueWeight = 1 + 0.015 * meanC * t;
  const weightedL = deltaL / lightnessWeight;
  const weightedC = deltaC / chromaWeight;
  const weightedH = deltaH / hueWeight;
  return Math.sqrt(Math.max(0, weightedL ** 2 + weightedC ** 2 + weightedH ** 2 + rotation * weightedC * weightedH));
};

export const getPerceptualColorDistance = (colorA, colorB) => deltaE2000(toLab(colorA), toLab(colorB));

// Game score: 100 is identical; each CIEDE2000 unit removes one point.
// Rounding here makes the displayed tenth and pass decision identical.
export const getColorMatchPercentage = (colorA, colorB) => {
  const score = Math.max(0, 100 - getPerceptualColorDistance(colorA, colorB));
  return Math.round(score * 10) / 10;
};
